import { h } from '@omni-torn/ui';
import {
  CLAIM_LIFE_CHOICES,
  CLAIM_LIFE_DEFAULT_S,
  claimFor,
  claimLifeLabel,
  cleanFollow,
  cleanRoomFilter,
  cleanRoomSort,
  countriesOf,
  fairFightFor,
  FOLLOW_CHOICES,
  followDue,
  followPerMinute,
  keyMeets,
  MAX_OPEN_CLAIMS,
  openClaims,
  readRankedWarList,
  readWarReport,
  ROOM_SORTS,
  scoreFromTotal,
  scoreOfStats,
  shapeRoom,
  warEndFor,
  warPhase,
  warTargetAt,
  warTargetSteps,
  WAR_DECAY_FROM_H,
  type Activity,
  type ClaimCall,
  type FollowEvery,
  type LiveView,
  type RankedWarInfo,
  type RoomFilter,
  type RoomMember,
  type RoomSort,
  type WarEstimate,
  type WarEstimatesAnswer,
  type WarReport,
} from '@omni-torn/shared';
import { readBattleStats, readFaction, type FactionMember } from '../read';
import { server, session, torn, TornError, tornCallsLeft } from '../session';
import { ago, box, duration, ext, facts, loading, num, problem, profileUrl, short, table, tct } from '../ui';
import { flightStatus, wantFlights, warFlightsBox } from './flights-view';

/**
 * Faction → War — the war room (website roadmap S04: TornStats' war page, FF Scouter's War Room, Torn Intel's live ranked
 * war tracker): the current or last ranked war — the score, a war-end calculator, both rosters with their status, last
 * action and Omni-Torn's best estimate (the player database), the enemy's fair fight against you, filters and sorting,
 * hit claims (S35: Claim / Queue / Unclaim, the claim life the leader sets, Wipe) and Rally shared with your faction
 * (/api/coord, the same board the extension's war page shows), our members' hits and respect (the Discord bot's count from the
 * faction's attack log, which it reads anyway) — and past wars, each with Torn's report of both sides when you open it.
 *
 * Where it comes from: the bot's live view (`/api/faction/live`, no calls of yours), the faction's ranked wars from the
 * same Torn call the page already makes (basic,rankedwars with your key), estimates from `/api/faction/war/estimates`,
 * your battle stats (Limited key, only in this browser) for fair fight, and a past war's report from Torn (your key) only
 * when you open it. Following the enemy with your own key (your choice: every 10–60 s) reads Torn's public member list of
 * their faction (`/faction/{id}?selections=basic`, which also carries the war's score) only while this page is open, on
 * the War tab and in view, within the site's share of your calls a minute (packages/shared war-room.ts `followDue`).
 * Nothing it reads is sent anywhere.
 */

const attackUrl = (id: number) => `https://www.torn.com/page.php?sid=attack&user2ID=${id}`;
const countdown = (untilMs: number, cls = '') => h('span', { class: `cd ${cls}`.trim(), 'data-until': String(untilMs), title: `${tct(untilMs)} TCT` }, duration((untilMs - Date.now()) / 1000));

// ---------------------------------------------------------------- what this browser remembers (the viewer's choices)

const UI_KEY = 'omni-torn.warroom';
interface Ui {
  filter: RoomFilter;
  sort: RoomSort;
  follow: FollowEvery;
}
function loadUi(): Ui {
  try {
    const raw = JSON.parse(localStorage.getItem(UI_KEY) ?? 'null') as Partial<Ui> | null;
    return { filter: cleanRoomFilter(raw?.filter), sort: cleanRoomSort(raw?.sort), follow: cleanFollow(raw?.follow) };
  } catch {
    return { filter: cleanRoomFilter(null), sort: 'okay', follow: 0 };
  }
}
const ui = loadUi();
function saveUi(): void {
  try {
    localStorage.setItem(UI_KEY, JSON.stringify(ui));
  } catch {
    /* this visit only */
  }
}

// ---------------------------------------------------------------- estimates and your fair fight

/** Estimates asked for (by id), and when; asked again after 10 minutes. */
const estimates = new Map<number, WarEstimate | null>();
let estimatesAt = 0;
let estimatesAsking: Promise<void> | null = null;
let estimatesProblem = '';
/** Past wars opened (their report, kept for the page's life: a finished war doesn't change). */
const reports = new Map<number, WarReport | string>();
const open = new Set<number>();

function wantEstimates(ids: number[], redraw: () => void): void {
  const missing = ids.filter((id) => !estimates.has(id));
  if (estimatesAsking || (!missing.length && Date.now() - estimatesAt < 10 * 60_000) || !ids.length) return;
  const ask = (missing.length ? missing : ids).slice(0, 200);
  estimatesAsking = server<WarEstimatesAnswer>('GET', `/api/faction/war/estimates?ids=${ask.join(',')}`)
    .then((a) => {
      for (const id of ask) estimates.set(id, null);
      for (const e of a.estimates) estimates.set(e.id, e);
      estimatesAt = Date.now();
      estimatesProblem = '';
    })
    .catch((err: Error) => {
      for (const id of ask) estimates.set(id, null);
      estimatesProblem = err.message;
    })
    .finally(() => {
      estimatesAsking = null;
      redraw();
    });
}

const METHOD: Record<string, string> = { fight: 'from fights', pool: 'from the reading pool', rank: 'a guess from their rank', spy: 'from a spy' };
function estimateCell(id: number): Node | string {
  const e = estimates.get(id);
  if (e === undefined) return h('span', { class: 'muted' }, '…');
  if (!e) return h('span', { class: 'muted' }, '·');
  return h('span', { title: `Omni-Torn’s estimate, ${METHOD[e.source] ?? e.source}${e.at ? `, ${ago(e.at)}` : ''}`, class: e.source === 'rank' ? 'muted' : '' }, `${e.source === 'rank' ? '~' : ''}${short(e.total)}`);
}
const estimateSort = (id: number) => estimates.get(id)?.total ?? -1;

/** Your battle score (read with your key, kept only in this page); null without a key that reads battle stats. */
let yourScore: number | null | undefined;
let scoreAsking = false;
function wantScore(redraw: () => void): void {
  if (yourScore !== undefined || scoreAsking) return;
  if (!keyMeets(session.get()?.access ?? null, { level: 3, user: ['battlestats'] })) {
    yourScore = null;
    return;
  }
  scoreAsking = true;
  torn('/user/', { selections: 'battlestats' }, 10 * 60_000)
    .then((b) => (yourScore = scoreOfStats(readBattleStats(b))))
    .catch(() => (yourScore = null))
    .finally(() => {
      scoreAsking = false;
      redraw();
    });
}
/** Your fair fight against a player, from Omni-Torn's estimate of them (two decimals), when both are known. */
function ffOf(id: number): number | null {
  const e = estimates.get(id);
  if (!e || !yourScore) return null;
  const ff = fairFightFor(yourScore, scoreFromTotal(e.total));
  return ff === null ? null : Math.round(ff * 100) / 100;
}
function ffCell(id: number): Node | string {
  const ff = ffOf(id);
  if (ff === null) return h('span', { class: 'muted' }, '·');
  return h('span', { class: ff >= 2.5 ? 't-green' : ff < 1.5 ? 'muted' : '', title: 'Your fair fight against them, from Omni-Torn’s estimate' }, ff.toFixed(2));
}

// ---------------------------------------------------------------- the rosters

type Who = RoomMember;
const activityOf = (s: string): Activity => (/^online$/i.test(s) ? 'online' : /^idle$/i.test(s) ? 'idle' : 'offline');
const fromTorn = (m: FactionMember): Who => ({ id: m.id, name: m.name, level: m.level, state: m.state, description: m.description, until: m.until, lastAction: m.lastActionAt, activity: activityOf(m.online) });

function statusCell(r: Who, trips?: Map<number, LiveView['trips'][number]>): Node {
  if ((r.state === 'Hospital' || r.state === 'Jail') && r.until && r.until > Date.now()) return h('span', { class: 'st-red' }, `${r.description && !/^in (hospital|jail)/i.test(r.description) ? `${r.description.replace(/ for .*$/, '')} · ` : `${r.state} · `}`, countdown(r.until));
  const t = trips?.get(r.id);
  if (t && t.leg !== 'there' && /travel/i.test(r.state)) {
    const where = t.leg === 'out' ? t.destination : 'Torn';
    if (t.earliest && t.latest) return h('span', { class: 'st-blue' }, `➤ ${where} · lands `, t.exact ? `${tct(t.latest * 1000)} TCT` : `${tct(t.earliest * 1000)}–${tct(t.latest * 1000)} TCT`);
    return h('span', { class: 'st-blue' }, `➤ ${where} · landing any moment`);
  }
  // No window from the bot: Omni-Torn's flight tracker (website roadmap S23), when it saw this flight.
  const tracked = trips ? flightStatus(r.id, r.state) : null;
  if (tracked) return tracked;
  return h('span', { class: r.state === 'Okay' ? 't-green' : /travel|abroad/i.test(r.state) ? 'st-blue' : '' }, r.description || r.state);
}
const statusOrder = (r: Who) => (r.state === 'Okay' ? 0 : r.state === 'Hospital' ? 1 + (r.until ?? 0) / 1e14 : 2);
const seenCell = (r: Who) => h('span', { class: `dot dot-${r.activity}`, title: r.activity }, r.lastAction ? ago(r.lastAction) : '—');

// ---------------------------------------------------------------- following the enemy with your own key

const follow: { factionId: number | null; at: number; members: Who[] | null; war: RankedWarInfo | null; name: string; problem: string; lastAsk: number; backoffUntil: number; asking: boolean } = {
  factionId: null,
  at: 0,
  members: null,
  war: null,
  name: '',
  problem: '',
  lastAsk: 0,
  backoffUntil: 0,
  asking: false,
};
/** The enemy faction the War tab shows now (set on each draw), and how to draw the tab again. */
let enemyNow: number | null = null;
let redrawWar: () => void = () => {};

/** A form field in the war room has the focus: a redraw now would take it away (the next one draws). */
export function warRoomBusy(): boolean {
  return !!(document.activeElement && document.activeElement.closest('[data-keep-focus]'));
}

/**
 * Called every second by the Faction page while the War tab is shown: reads the enemy's member list with your key when
 * you follow them and a read is due (only while the page is in view; within the site's share of your calls a minute).
 */
export function warTick(): void {
  if (!enemyNow) return;
  if (follow.factionId !== enemyNow) Object.assign(follow, { factionId: enemyNow, at: 0, members: null, war: null, problem: '', lastAsk: 0, backoffUntil: 0 });
  const now = Date.now();
  if (!followDue({ every: ui.follow, visible: document.visibilityState === 'visible', lastAsk: follow.lastAsk, backoffUntil: follow.backoffUntil, callsLeft: tornCallsLeft(now), asking: follow.asking }, now)) return;
  const id = enemyNow;
  follow.asking = true;
  follow.lastAsk = now;
  torn(`/faction/${id}`, { selections: 'basic' }, Math.max(5_000, ui.follow * 1000 - 1_000))
    .then((body) => {
      if (follow.factionId !== id) return;
      const f = readFaction(body);
      if (!f) throw new TornError(0, 'Torn didn’t send their member list.');
      follow.members = f.members.map(fromTorn);
      follow.name = f.name;
      follow.war = readRankedWarList(body)[0] ?? null;
      follow.at = Date.now();
      follow.problem = '';
    })
    .catch((err: Error) => {
      if (follow.factionId !== id) return;
      follow.problem = err.message;
      // Torn's "too many requests": rest a minute.
      if (err instanceof TornError && err.code === 5) follow.backoffUntil = Date.now() + 60_000;
    })
    .finally(() => {
      follow.asking = false;
      if (!warRoomBusy()) redrawWar();
    });
}

// ---------------------------------------------------------------- calls and rallies (/api/coord)

interface CoordState {
  calls: ClaimCall[];
  rally: { targetId: number | null; note: string; by: { id: number; name: string } } | null;
  /** Hit claims (S35): how long one lasts (s), how many a member may have open, how many may wait for one target. */
  claims?: { life: number; maxOpen: number; maxQueue: number };
  /** The viewer is the faction's leader or co-leader: may set the claim life and wipe the claims. */
  leader?: boolean;
}
let coord: CoordState | null = null;
let coordAt = 0;
let coordAsking = false;
let coordProblem = '';
let coordNote = '';

function wantCoord(redraw: () => void, force = false): void {
  if (coordAsking || (!force && Date.now() - coordAt < 10_000) || document.visibilityState !== 'visible') return;
  coordAsking = true;
  coordAt = Date.now();
  server<CoordState>('GET', '/api/coord')
    .then((s) => {
      coord = s;
      coordProblem = '';
      // The last button's answer ("Called.") has been on screen a while: the board itself says it now.
      coordNote = '';
    })
    .catch((err: Error) => {
      coordProblem = err.message;
    })
    .finally(() => {
      coordAsking = false;
      redraw();
    });
}

async function coordSend(path: '/api/coord/call' | '/api/coord/rally' | '/api/coord/claims/life' | '/api/coord/claims/wipe', body: Record<string, unknown>, redraw: () => void): Promise<void> {
  try {
    const r = await server<{ ok?: boolean; message?: string; state?: CoordState }>('POST', path, body);
    if (r.state) coord = r.state;
    coordNote = r.message ?? '';
  } catch (err) {
    coordNote = (err as Error).message;
  }
  coordAt = Date.now();
  redraw();
}

function coordButton(label: string, title: string, act: () => Promise<void>, attrs: Record<string, string> = {}): HTMLButtonElement {
  const b = h('button', { type: 'button', class: 'btn-small btn-gap', title, ...attrs }, label);
  b.addEventListener('click', (e) => {
    // A call goes out under your name: a real click only.
    if (!e.isTrusted) return;
    b.disabled = true;
    void act();
  });
  return b;
}

/**
 * One target's claim (S35 — the same board the extension's war page shows): Claim when free; yours: Unclaim, how long
 * it has left and who waits; someone else's: their name and time left, and Queue (or Leave, with your place in line).
 */
function callCell(r: Who, meId: number | null, redraw: () => void): Node {
  if (!coord) return h('span', { class: 'muted' }, coordProblem ? '' : '…');
  const c = claimFor(coord.calls, r.id, meId);
  const med = coord.calls.find((x) => x.kind === 'med' && x.targetId === r.id);
  const rallied = coord.rally?.targetId === r.id;
  const send = (on: boolean) => () => coordSend('/api/coord/call', { targetId: r.id, kind: 'call', on }, redraw);
  const waiting = c.queue.length ? h('span', { class: 'chip', 'data-claim': 'queue', title: `Waiting: ${c.queue.map((q) => q.name).join(', ')}` }, `+${c.queue.length} waiting`) : null;
  let claim: Node[];
  if (!c.holder) claim = [coordButton('Claim', 'Claim them: your faction sees you’re hitting them', send(true), { 'data-action': 'call' })];
  else if (c.mine) claim = [coordButton('Unclaim', c.queue.length ? `Let go — ${c.queue[0]!.name} takes it over` : 'Take your claim back', send(false), { 'data-action': 'uncall' }), countdown(c.holder.until, 'claim-left'), ...(waiting ? [waiting] : [])];
  else
    claim = [
      h('span', { class: 'chip', 'data-claim': 'holder', title: c.holder.ally ? 'Claimed by an allied faction' : 'Claimed — they’re hitting them' }, `☎ ${c.holder.name}${c.holder.ally ? ' (ally)' : ''} `, countdown(c.holder.until, 'claim-left')),
      ...(waiting ? [waiting] : []),
      ...(c.holder.ally
        ? []
        : c.place
          ? [coordButton(`Leave (#${c.place})`, `You’re ${c.place === 1 ? 'next' : `number ${c.place}`} in line — leave it`, send(false), { 'data-action': 'leave' })]
          : [coordButton('Queue', 'Wait in line: you get the claim when they let go or it runs out', send(true), { 'data-action': 'queue' })]),
    ];
  return h(
    'span',
    { class: 'war-calls', 'data-call': String(r.id) },
    ...claim,
    med ? h('span', { class: 'chip', title: 'Med deal — leave them alone' }, `✚ ${med.by.name}`) : null,
    coordButton(rallied ? '⚑ Stop' : '⚑', rallied ? 'Stop the rally' : 'Rally your faction on them', () => coordSend('/api/coord/rally', { targetId: r.id, note: r.name, on: !rallied }, redraw), { 'data-action': rallied ? 'unrally' : 'rally' }),
  );
}

/** "Wipe all claims" asks once more before it goes (a second real click within 5 s). */
let wipeArmed = 0;

/**
 * The claims line above the enemy roster: how long a claim lasts and how many you have open; for the faction's leader or
 * co-leader, the claim life to pick (30 s – 15 min) and Wipe all claims.
 */
function claimsLine(meId: number | null, redraw: () => void): HTMLElement {
  const line = h('div', { class: 'war-claims muted small', 'data-war': 'claims' });
  if (!coord) return line;
  const life = coord.claims?.life ?? CLAIM_LIFE_DEFAULT_S;
  const max = coord.claims?.maxOpen ?? MAX_OPEN_CLAIMS;
  const claimed = coord.calls.filter((x) => x.kind === 'call' && !x.ally).length;
  line.append(h('span', { 'data-claims': 'info' }, `Claims last ${claimLifeLabel(life)} · you have ${openClaims(coord.calls, meId)} of ${max} open (claimed or waiting) · ${claimed} claimed in all.`));
  if (coord.leader) {
    const pick = h('select', { 'data-claims': 'life', 'aria-label': 'How long a claim lasts' }, ...[...new Set([...CLAIM_LIFE_CHOICES, life])].sort((a, b) => a - b).map((s) => h('option', { value: String(s), ...(s === life ? { selected: true } : {}) }, claimLifeLabel(s)))) as HTMLSelectElement;
    pick.addEventListener('change', (e) => {
      if (!e.isTrusted) return;
      pick.disabled = true;
      void coordSend('/api/coord/claims/life', { seconds: Number(pick.value) }, redraw);
    });
    const armed = Date.now() - wipeArmed < 5_000;
    const wipe = coordButton(armed ? `Sure? Wipe ${claimed}` : 'Wipe all claims', 'Leaders: every claim and every line goes (med deals and the rally stay)', async () => {
      if (!armed) {
        wipeArmed = Date.now();
        redraw();
        return;
      }
      wipeArmed = 0;
      await coordSend('/api/coord/claims/wipe', {}, redraw);
    }, { 'data-action': armed ? 'wipe-sure' : 'wipe' });
    line.append(h('span', { class: 'war-claims-lead', 'data-keep-focus': '' }, h('label', {}, 'Claim life ', pick), wipe));
  }
  return line;
}

// ---------------------------------------------------------------- the filter bar (kept between redraws)

let bar: { el: HTMLElement; place: HTMLSelectElement; count: HTMLElement; follow: HTMLSelectElement; followNote: HTMLElement } | null = null;
let shapeAgain: () => void = () => {};

function filterBar(): NonNullable<typeof bar> {
  if (bar) return bar;
  const changed = () => {
    saveUi();
    shapeAgain();
  };
  const check = (label: string, get: () => boolean, set: (v: boolean) => void, name: string) => {
    const c = h('input', { type: 'checkbox', 'data-filter': name, ...(get() ? { checked: true } : {}) }) as HTMLInputElement;
    c.addEventListener('change', () => {
      set(c.checked);
      changed();
    });
    return h('label', { class: 'target-check' }, c, ` ${label}`);
  };
  const act = (a: Activity, label: string) =>
    check(label, () => ui.filter.activity.includes(a), (v) => (ui.filter.activity = v ? [...ui.filter.activity, a] : ui.filter.activity.filter((x) => x !== a)), `act-${a}`);
  const ff = (k: 'ffMin' | 'ffMax', label: string) => {
    const i = h('input', { type: 'number', min: '1', max: '3', step: '0.1', placeholder: k === 'ffMin' ? '1' : '3', 'data-filter': k, value: ui.filter[k] === null ? '' : String(ui.filter[k]) }) as HTMLInputElement;
    // On change (Enter or leaving the box), not each key: the list moves under you otherwise.
    i.addEventListener('change', () => {
      ui.filter = cleanRoomFilter({ ...ui.filter, [k]: i.value });
      changed();
    });
    return h('label', { class: 'target-field' }, label, i);
  };
  const place = h('select', { 'data-filter': 'place', 'aria-label': 'Where they are' }) as HTMLSelectElement;
  place.addEventListener('change', () => {
    ui.filter = { ...ui.filter, place: place.value };
    changed();
  });
  const sort = h('select', { 'data-filter': 'sort', 'aria-label': 'Sort by' }, ...ROOM_SORTS.map((s) => h('option', { value: s.id }, s.label))) as HTMLSelectElement;
  sort.value = ui.sort;
  sort.addEventListener('change', () => {
    ui.sort = cleanRoomSort(sort.value);
    changed();
  });
  const followSel = h(
    'select',
    { 'data-war': 'follow', 'aria-label': 'Follow their roster' },
    ...FOLLOW_CHOICES.map((s) => h('option', { value: String(s) }, s ? `with my key, every ${s} s (${followPerMinute(s)} calls a minute)` : 'with the Discord bot’s watch (no calls of yours)')),
  ) as HTMLSelectElement;
  followSel.value = String(ui.follow);
  followSel.addEventListener('change', () => {
    ui.follow = cleanFollow(followSel.value);
    follow.lastAsk = 0;
    saveUi();
    redrawWar();
  });
  const count = h('span', { class: 'muted small', 'data-war': 'count' });
  const followNote = h('span', { class: 'muted small', 'data-war': 'follow-note' });
  const el = h(
    'div',
    { class: 'war-filters', 'data-keep-focus': '' },
    h('div', { class: 'target-fields' }, h('label', { class: 'target-field' }, 'Follow their roster', followSel), followNote),
    h(
      'div',
      { class: 'target-fields' },
      check('Okay only', () => ui.filter.okayOnly, (v) => (ui.filter.okayOnly = v), 'okay'),
      check('Hide fallen & federal', () => ui.filter.hideFallen, (v) => (ui.filter.hideFallen = v), 'fallen'),
      act('online', 'Online'),
      act('idle', 'Idle'),
      act('offline', 'Offline'),
    ),
    h('div', { class: 'target-fields' }, h('label', { class: 'target-field' }, 'Where', place), ff('ffMin', 'FF from'), ff('ffMax', 'FF to'), h('label', { class: 'target-field' }, 'Sort', sort), count),
  );
  bar = { el, place, count, follow: followSel, followNote };
  return bar;
}

function fillPlaces(sel: HTMLSelectElement, countries: string[]): void {
  const want = [['any', 'Anywhere'], ['torn', 'In Torn'], ['away', 'Flying or abroad'], ...[...new Set([...countries, ...(['any', 'torn', 'away'].includes(ui.filter.place) ? [] : [ui.filter.place])])].map((c) => [c, c])];
  const sig = want.map((w) => w[0]).join('|');
  if (sel.dataset.sig !== sig) {
    sel.replaceChildren(...want.map(([v, t]) => h('option', { value: v! }, t!)));
    sel.dataset.sig = sig;
  }
  sel.value = ui.filter.place;
}

// ---------------------------------------------------------------- the War tab

/** The War tab. `members`: Torn's member list (with the bot's newer statuses). */
export function warSection(live: LiveView | null, members: FactionMember[], factionId: number, wars: RankedWarInfo[], redraw: () => void): Node[] {
  redrawWar = redraw;
  const now = Date.now() / 1000;
  const current = live?.war ? (wars.find((w) => w.id === live.war!.id) ?? null) : (wars.find((w) => warPhase(w, now) !== 'over') ?? null);
  // The bot's scores are newer than Torn's answer when the page opened; your own key's read may be newer still.
  let war: RankedWarInfo | null = live?.war ? { id: live.war.id, start: live.war.start, end: live.war.end, target: live.war.target, winner: current?.winner ?? null, factions: live.war.factions } : current;
  let scoreFrom = live?.war ? `the Discord bot, ${ago(live.at * 1000)}` : '';
  const enemyId = war?.factions.find((f) => f.id !== factionId)?.id ?? live?.enemy?.id ?? null;
  enemyNow = enemyId;
  const followed = ui.follow && follow.factionId === enemyId && follow.members ? follow : null;
  if (followed?.war && war && followed.war.id === war.id && followed.at > (live?.at ?? 0) * 1000) {
    war = { ...war, factions: followed.war.factions.length === 2 ? followed.war.factions : war.factions, end: followed.war.end ?? war.end, target: followed.war.target ?? war.target };
    scoreFrom = `your key, ${ago(followed.at)}`;
  }
  const last = wars.find((w) => warPhase(w, now) === 'over') ?? null;
  const out: Node[] = [];
  if (war) {
    out.push(scoreBox(war, factionId, now, 'Ranked war', scoreFrom));
    if (warPhase(war, now) !== 'over' && war.target) out.push(endCalculator(war, factionId, now));
  } else out.push(box('Ranked war', [h('p', { class: 'muted' }, 'No ranked war now.'), last ? h('p', {}, 'The last one: ', lastLine(last, factionId)) : null]));

  // Both rosters.
  const enemyName = war?.factions.find((f) => f.id === enemyId)?.name ?? live?.enemy?.name ?? (followed?.name || 'Enemy');
  const watching = !!live?.enemy && (!enemyId || live.enemy.id === enemyId);
  const botRows: Who[] = watching ? live!.enemyMembers.map((m) => ({ id: m.id, name: m.name, level: m.level, state: m.state, description: m.description, until: m.until ? m.until * 1000 : null, lastAction: m.lastAction ? m.lastAction * 1000 : null, activity: m.activity })) : [];
  const useOwn = !!followed && followed.at > (watching && live!.enemyAt ? live!.enemyAt * 1000 : 0);
  const theirs: Who[] = useOwn ? followed!.members! : botRows;
  const ours: Who[] = members.map(fromTorn);
  wantEstimates([...theirs, ...ours].map((x) => x.id), redraw);
  // The flight tracker (S23): the enemy's flights Omni-Torn kept — landing windows by way of flying, "airstrip/business likely".
  wantFlights(theirs.map((x) => x.id), redraw);
  wantScore(redraw);
  if (enemyId) wantCoord(redraw);
  const trips = new Map((live?.trips ?? []).map((t) => [t.id, t]));
  if (enemyId || live?.enemy) out.push(enemyBox(enemyId, enemyName, theirs, { live, watching, useOwn, trips }, redraw), warFlightsBox(theirs));
  out.push(oursBox(live, war, ours, enemyName, now));
  out.push(pastBox(wars.filter((w) => warPhase(w, now) === 'over'), factionId, redraw));
  return out;
}

function enemyBox(enemyId: number | null, enemyName: string, theirs: Who[], o: { live: LiveView | null; watching: boolean; useOwn: boolean; trips: Map<number, LiveView['trips'][number]> }, redraw: () => void): HTMLElement {
  const b = filterBar();
  const meId = session.me()?.playerId ?? session.get()?.playerId ?? null;
  // Where the roster comes from, in plain words.
  const from = o.useOwn
    ? `Read with your key every ${ui.follow} seconds while this page is in view — last ${ago(follow.at)}.`
    : o.watching
      ? `Read by the Discord bot every 30 seconds${o.live!.enemyAt ? ` — last ${ago(o.live!.enemyAt * 1000)}` : ''}.`
      : ui.follow
        ? 'Reading their member list with your key…'
        : o.live
          ? 'The Discord bot isn’t watching them yet — it starts within a minute of this page asking. Or follow them with your key above.'
          : 'The Discord bot’s watch isn’t available — follow them with your key above to see their roster.';
  b.followNote.textContent = ui.follow
    ? `${follow.problem ? `Torn said: ${follow.problem} ` : ''}Only while this page is open and on screen, within this site’s share of your Torn calls (Torn allows 100 a minute across all your tools).`
    : '';
  if (!enemyId) b.follow.disabled = true;
  else b.follow.disabled = false;
  fillPlaces(b.place, countriesOf(theirs));
  const holder = h('div', {});
  const rallyLine = h('p', { class: 'war-rally', 'data-war': 'rally' });
  const noteLine = h('p', { class: 'muted small', 'data-war': 'coord-note' });
  const claimsHolder = h('div', {});
  const draw = () => {
    const nowMs = Date.now();
    const rows = shapeRoom(theirs, ui.filter, ui.sort, ffOf, nowMs);
    b.count.textContent = theirs.length ? `Showing ${rows.length} of ${theirs.length}` : '';
    const r = coord?.rally;
    rallyLine.replaceChildren(...(r ? [`⚑ Rally: ${r.note || `player ${r.targetId}`} (by ${r.by.name}) `, r.targetId ? ext(attackUrl(r.targetId), 'Attack') : '', ' ', coordButton('Stop', 'Stop the rally', () => coordSend('/api/coord/rally', { targetId: r.targetId, note: '', on: false }, redraw), { 'data-action': 'stop-rally' })] : []));
    rallyLine.hidden = !r;
    noteLine.textContent = coordProblem ? `Calls: ${coordProblem}` : coordNote;
    claimsHolder.replaceChildren(claimsLine(meId, redraw));
    holder.replaceChildren(
      table(
        rows,
        [
          { label: 'Member', get: (m) => ext(profileUrl(m.id), m.name), sort: (m) => m.name.toLowerCase() },
          { label: 'Lvl', get: (m) => String(m.level), sort: (m) => m.level, cls: 'r' },
          { label: 'Estimate', get: (m) => estimateCell(m.id), sort: (m) => estimateSort(m.id), cls: 'r' },
          { label: 'FF', get: (m) => ffCell(m.id), sort: (m) => ffOf(m.id) ?? -1, cls: 'r' },
          { label: 'Status', get: (m) => statusCell(m, o.trips), sort: statusOrder },
          { label: 'Seen', get: seenCell, sort: (m) => -(m.lastAction ?? 0) },
          { label: 'Claim', get: (m) => callCell(m, meId, redraw) },
          { label: '', get: (m) => (m.state === 'Okay' ? ext(attackUrl(m.id), 'Attack') : ''), cls: 'r' },
        ],
        // The sort picked above orders the list; a header click sorts it another way until the pick changes.
        { initial: -1, empty: theirs.length ? 'No one matches the filters.' : 'Not read yet.', remember: `war-theirs-${ui.sort}` },
      ),
    );
  };
  shapeAgain = draw;
  draw();
  return box(
    `${enemyName} (${theirs.length || '—'})`,
    [
      h('p', { class: 'muted small', 'data-war': 'source' }, from, ' Estimates: Omni-Torn’s own (~ = a guess from their rank).', yourScore === null ? ' Fair fight needs your battle stats (a Limited key).' : '', estimatesProblem ? ` ${estimatesProblem}` : ''),
      b.el,
      rallyLine,
      claimsHolder,
      noteLine,
      holder,
    ],
    { id: 'war-theirs', ...(enemyId ? { extra: ext(`https://www.torn.com/factions.php?step=profile&ID=${enemyId}`, 'Their page ↗') } : {}) },
  );
}

function oursBox(live: LiveView | null, war: RankedWarInfo | null, ours: Who[], enemyName: string, now: number): HTMLElement {
  const hits = live?.warHits && war && live.warHits.warId === war.id ? live.warHits : null;
  const byId = new Map((hits?.members ?? []).map((m) => [m.id, m]));
  const totals = (hits?.members ?? []).reduce((a, m) => ({ hits: a.hits + m.hits, respect: a.respect + m.respect }), { hits: 0, respect: 0 });
  const running = !!war && warPhase(war, now) === 'on';
  return box(
    `Us (${ours.length})`,
    [
      running
        ? h('p', { class: 'muted small', 'data-war': 'hits-note' }, hits?.problem ? hits.problem : hits ? `Hits and respect on ${enemyName} counted by the Discord bot from the faction’s attack log since ${tct(hits.since * 1000)} TCT${hits.at ? ` (read ${ago(hits.at * 1000)})` : ''}: ${num(totals.hits)} hits, ${num(totals.respect, 2)} respect.` : 'Hits show once the Discord bot has read the faction’s attack log (it needs a member’s key with the faction’s API access).')
        : null,
      table(
        ours,
        [
          { label: 'Member', get: (r) => ext(profileUrl(r.id), r.name), sort: (r) => r.name.toLowerCase() },
          { label: 'Lvl', get: (r) => String(r.level), sort: (r) => r.level, cls: 'r' },
          { label: 'Estimate', get: (r) => estimateCell(r.id), sort: (r) => estimateSort(r.id), cls: 'r' },
          { label: 'Status', get: (r) => statusCell(r), sort: statusOrder },
          { label: 'Seen', get: seenCell, sort: (r) => -(r.lastAction ?? 0) },
          ...(running
            ? [
                { label: 'Hits', get: (r: Who) => (byId.get(r.id) ? num(byId.get(r.id)!.hits) : '·'), sort: (r: Who) => byId.get(r.id)?.hits ?? -1, cls: 'r' },
                { label: 'Respect', get: (r: Who) => (byId.get(r.id) ? num(byId.get(r.id)!.respect, 2) : '·'), sort: (r: Who) => byId.get(r.id)?.respect ?? -1, cls: 'r' },
                { label: 'Assists', get: (r: Who) => (byId.get(r.id)?.assists ? num(byId.get(r.id)!.assists) : '·'), sort: (r: Who) => byId.get(r.id)?.assists ?? -1, cls: 'r' },
              ]
            : []),
        ],
        { initial: running ? 6 : 3, desc: running, empty: 'No one here.', remember: running ? 'war-ours-hits' : 'war-ours' },
      ),
    ],
    { id: 'war-ours' },
  );
}

function scoreBox(w: RankedWarInfo, us: number, now: number, title: string, from: string): HTMLElement {
  const our = w.factions.find((f) => f.id === us);
  const them = w.factions.find((f) => f.id !== us);
  const phase = warPhase(w, now);
  const total = (our?.score ?? 0) + (them?.score ?? 0);
  const bar = h('div', { class: 'war-bar', title: `${num(our?.score ?? 0)} – ${num(them?.score ?? 0)}` }, h('span', {}));
  (bar.firstChild as HTMLElement).style.width = `${total ? Math.round(((our?.score ?? 0) / total) * 100) : 50}%`;
  const lead = (our?.score ?? 0) - (them?.score ?? 0);
  return box(
    title,
    [
      h(
        'p',
        { class: 'war-score', 'data-war': 'score' },
        h('span', {}, h('strong', {}, our?.name ?? 'Us'), ` ${num(our?.score ?? 0)}`),
        h('span', {}, '–'),
        h('span', {}, `${num(them?.score ?? 0)} `, h('strong', {}, them?.name ?? 'Them')),
        h('span', { class: `lead ${lead > 0 ? 't-green' : lead < 0 ? 'st-red' : ''}` }, lead > 0 ? `ahead by ${num(lead)}` : lead < 0 ? `behind by ${num(-lead)}` : 'level'),
      ),
      bar,
      h(
        'p',
        { class: 'muted small' },
        phase === 'soon' ? h('span', {}, 'Starts in ', countdown(w.start * 1000), ` (${tct(w.start * 1000)} TCT)`) : phase === 'on' ? `Running ${duration(now - w.start)} (since ${tct(w.start * 1000)} TCT)` : `${tct(w.start * 1000)} – ${tct((w.end ?? w.start) * 1000)} TCT`,
        w.target ? ` · target ${num(w.target)}` : '',
        our?.chain !== null && our?.chain !== undefined ? ` · our chain ${num(our.chain)}` : '',
        them?.chain !== null && them?.chain !== undefined ? ` · theirs ${num(them.chain)}` : '',
        from && phase !== 'over' ? h('span', { 'data-war': 'score-from' }, ` · score from ${from}`) : '',
      ),
    ],
    { id: 'war-score' },
  );
}

// ---------------------------------------------------------------- the war-end calculator

/** The lead typed into "If the lead were" (kept between redraws; empty = the lead now). */
let whatIf: HTMLInputElement | null = null;

function endCalculator(w: RankedWarInfo, us: number, now: number): HTMLElement {
  const our = w.factions.find((f) => f.id === us);
  const them = w.factions.find((f) => f.id !== us);
  const lead = (our?.score ?? 0) - (them?.score ?? 0);
  const needed = warTargetAt(w, Math.max(now, w.start)) ?? 0;
  const ahead = lead > 0 ? 'we win' : 'they win';
  const endsAt = (l: number): Node => {
    const at = warEndFor(w, l, now);
    if (!l) return h('span', {}, 'only once someone leads');
    if (at === null) return h('span', {}, '—');
    if (at <= Math.max(now, w.start)) return h('span', { class: 'st-red' }, `as soon as Torn counts it (the lead is enough)`);
    return h('span', {}, `about ${tct(at * 1000)} TCT (in `, countdown(at * 1000), ')');
  };
  if (!whatIf) {
    whatIf = h('input', { type: 'number', min: '0', step: '1', placeholder: 'a lead', 'data-war': 'what-if', 'aria-label': 'If the lead were' }) as HTMLInputElement;
  }
  const input = whatIf;
  const answer = h('span', { 'data-war': 'what-if-answer' });
  const showWhatIf = () => {
    const v = Number(input.value);
    answer.replaceChildren(input.value.trim() && Number.isFinite(v) && v > 0 ? h('span', {}, ' → the war ends ', endsAt(v)) : '');
  };
  input.oninput = showWhatIf;
  showWhatIf();
  const steps = warTargetSteps(w, now, 6);
  return box(
    'When does it end?',
    [
      facts([
        ['Lead needed now', h('span', { 'data-war': 'needed' }, num(needed), w.target && needed < w.target ? h('span', { class: 'muted' }, ` (of ${num(w.target)})`) : '')],
        ['If the lead stays', lead ? h('span', { 'data-war': 'ends' }, `${num(Math.abs(lead))} (${ahead}) — `, endsAt(lead)) : h('span', { 'data-war': 'ends' }, 'Level — it ends only once someone leads.')],
        ['To end it now', h('span', { 'data-war': 'to-end' }, lead >= 0 ? `we need ${num(Math.max(0, needed - lead))} more lead` : `they need ${num(Math.max(0, needed + lead))} more lead (we need ${num(needed - lead)} to win now)`)],
      ]),
      h('div', { class: 'calc-row', 'data-keep-focus': '' }, h('label', { class: 'target-field' }, 'If the lead were', input), answer),
      steps.length
        ? h(
            'p',
            { class: 'muted small', 'data-war': 'steps' },
            `The lead needed drops by 1% of the target every hour from ${WAR_DECAY_FROM_H} hours in: `,
            steps.map((s) => `${tct(s.at * 1000)} → ${num(s.target)}`).join(' · '),
            '. Torn’s own war page has the final word.',
          )
        : null,
    ],
    { id: 'war-end' },
  );
}

// ---------------------------------------------------------------- past wars

function lastLine(w: RankedWarInfo, us: number): Node {
  const our = w.factions.find((f) => f.id === us);
  const them = w.factions.find((f) => f.id !== us);
  const won = w.winner === us;
  return h('span', {}, `against ${them?.name ?? 'someone'}, `, h('span', { class: w.winner ? (won ? 't-green' : 'st-red') : '' }, w.winner ? (won ? 'won' : 'lost') : 'ended'), ` ${num(our?.score ?? 0)} – ${num(them?.score ?? 0)}`, w.end ? h('span', { class: 'muted' }, ` · ${new Date(w.end * 1000).toISOString().slice(0, 10)}`) : null);
}

function pastBox(past: RankedWarInfo[], us: number, redraw: () => void): HTMLElement {
  if (!past.length) return box('Past wars', h('p', { class: 'muted' }, 'None that Torn lists.'), { id: 'war-past' });
  return box(
    `Past wars (${past.length})`,
    [
      h('p', { class: 'muted small' }, 'Payouts, each member’s respect, fair fight and outside hits, and how members do across wars: the Wars tab.'),
      h(
        'ul',
        { class: 'war-past' },
        ...past.map((w) => {
          const btn = h('button', { type: 'button', class: 'btn-small', 'data-war-open': String(w.id) }, open.has(w.id) ? 'Hide' : 'Details');
          btn.addEventListener('click', () => {
            if (open.has(w.id)) open.delete(w.id);
            else {
              open.add(w.id);
              // Torn's report, asked once (your key): every member of both sides.
              if (!reports.has(w.id))
                void torn(`/torn/${w.id}`, { selections: 'rankedwarreport' }, 24 * 3_600_000)
                  .then((b) => reports.set(w.id, readWarReport(b, w.id) ?? 'Torn sent no report for this war.'))
                  .catch((err: Error) => reports.set(w.id, err.message))
                  .finally(redraw);
            }
            redraw();
          });
          return h('li', {}, lastLine(w, us), btn, open.has(w.id) ? reportView(w.id, us) : null);
        }),
      ),
    ],
    { id: 'war-past' },
  );
}

function reportView(id: number, us: number): HTMLElement {
  const r = reports.get(id);
  if (r === undefined) return h('div', { class: 'war-report' }, loading('Asking Torn for the war’s report…'));
  if (typeof r === 'string') return h('div', { class: 'war-report' }, problem(new Error(r)));
  const sides = [...r.factions].sort((a, b) => Number(b.id === us) - Number(a.id === us));
  return h(
    'div',
    { class: 'war-report', 'data-war-report': String(id) },
    ...sides.map((s) =>
      h(
        'div',
        {},
        h('h3', {}, `${s.name}: ${num(s.score)} score · ${num(s.attacks)} attacks`, r.winner === s.id ? h('span', { class: 't-green' }, ' · won') : null),
        table(
          s.members,
          [
            { label: 'Member', get: (m) => ext(profileUrl(m.id), m.name), sort: (m) => m.name.toLowerCase() },
            { label: 'Lvl', get: (m) => num(m.level), sort: (m) => m.level ?? 0, cls: 'r' },
            { label: 'Attacks', get: (m) => num(m.attacks), sort: (m) => m.attacks, cls: 'r' },
            { label: 'Score', get: (m) => num(m.score, 2), sort: (m) => m.score, cls: 'r' },
          ],
          { initial: 3, remember: `war-report-${s.id === us ? 'us' : 'them'}` },
        ),
      ),
    ),
  );
}
